import { useEffect, FC } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "shared/ui/shadcn/select";

import uniqBy from "lodash/uniqBy";

import {
  TIMEZONES as STANDARD_TIMEZONES,
  DST_TIMEZONES,
} from "shared/lib/constants/timezones";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { clsxMerge } from "shared/lib/helpers/styles";
import { getIsDST } from "shared/lib/helpers/date";

export const defaultTimezoneAbbr = getIsDST() ? "EDT" : "EST";
export const SCHEDULER_TIMEZONES = getIsDST()
  ? DST_TIMEZONES
  : STANDARD_TIMEZONES;

/**
 * Select the timezone for the follow-up email
 *
 * @param value{string} - The timezone to select (e.g. "EST")
 * @param onChange{function} - The function to call when the value changes
 * @returns
 */
export const SelectTimezone: FC<
  PropsWithClassNameI & { value: string; onChange?: (value: string) => void }
> = ({ className, value, onChange }) => {
  const TIMEZONES = SCHEDULER_TIMEZONES;

  useEffect(() => {
    if (value) return;

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const defaultTimezone =
      TIMEZONES.find((tz) => tz.tzCode === userTimezone)?.abbr ||
      defaultTimezoneAbbr;

    onChange?.(defaultTimezone);
  }, []);

  const handleTimezoneChange = (value: string) => {
    const selectedOption = TIMEZONES.find((tz) => tz.fullZoneName === value);

    onChange?.(selectedOption?.abbr || "");
  };

  const options = uniqBy(TIMEZONES, "fullZoneName");

  return (
    <Select
      value={options.find((tz) => tz.abbr === value)?.fullZoneName || ""}
      onValueChange={handleTimezoneChange}
    >
      <SelectTrigger
        className={clsxMerge("w-[80px] focus:ring-transparent", className)}
      >
        <SelectValue placeholder="Timezone">{value}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((timezone) => (
          <SelectItem key={timezone.fullZoneName} value={timezone.fullZoneName}>
            {timezone.fullZoneName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
